import { _ as _sliced_to_array } from "@swc/helpers/_/_sliced_to_array";
import { jsx as _jsx, jsxs as _jsxs } from "react/jsx-runtime";
import { useCallbackExperiment } from "@million/lint/runtime";
import { Core } from "@faire/design-tokens";
import { getFilteredOptions, hasSelectedOptions, recursivelyClearFilter, recursivelyToggleFilter, selectIfSelectedAllSubOptions, updateAllSelectableSubOptions } from "@faire/retailer-visitor-shared/components/Filters/FilterUtils";
import { useFilterSectionSelectedChange } from "@faire/retailer-visitor-shared/components/Filters/useFilterSectionSelectedChange";
import { useFilterStore } from "@faire/retailer-visitor-shared/components/Filters/useFilterStore";
import { handleMouseDown } from "@faire/retailer-visitor-shared/lib/handleMouseDown";
import { useIsSearchPage } from "@faire/retailer-visitor-shared/lib/useIsSearchPage";
import { useAssignSettingEnableOnMouseDownFilters, useGetSettingEnableOnMouseDownFilters } from "@faire/retailer-visitor-shared/settings/useSettingEnableOnMouseDownFilters";
import { useSettingMillionLintFiltersOptimization } from "@faire/retailer-visitor-shared/settings/useSettingMillionLintFiltersOptimization";
import { useStrictLocalization } from "@faire/web--source/common/localization/localize";
import { Button } from "@faire/web--source/slate/Button";
import Checkbox from "@faire/web--source/slate/Checkbox";
import { Chevron } from "@faire/web--source/slate/icons/Chevron";
import { Row } from "@faire/web--source/slate/Layout";
import Radio from "@faire/web--source/slate/Radio";
import { getSpacing, Spacer } from "@faire/web--source/slate/spacing";
import { Span, Typography } from "@faire/web--source/slate/Typography";
import { IFilterSection } from "@faire/web-api--source/indigofair/data/IFilterSection";
import React, { useState } from "react";
import styled from "styled-components";
export var FilterOption = function(param) {
    var isSubOption = param.isSubOption, option = param.option, section = param.section, searchValue = param.searchValue, renderOption = param.renderOption, onChange = param.onChange, onExpand = param.onExpand, _param_showUnformattedResultsCount = param.showUnformattedResultsCount, showUnformattedResultsCount = _param_showUnformattedResultsCount === void 0 ? false : _param_showUnformattedResultsCount;
    var shouldMemoMillionLint = useSettingMillionLintFiltersOptimization();
    var assignEnableOnMouseDownFilters = useAssignSettingEnableOnMouseDownFilters();
    var isSearchPage = useIsSearchPage();
    var isMouseDownEnabled = useGetSettingEnableOnMouseDownFilters() && isSearchPage;
    /** Million Experiment: This function was originally passed as an unstable prop to FilterOption.
     * However, it's hard to convert it to an experiment so we moved the function here and wrapped it with useCallbackExperiment.
     * When the experiment is not running, the performance remains the same because both renderSubOptions and renderOption are unstable (view PR for definition)
     * Since both are unstable, both will cause an re-render to occur due to prop changes.
     * In terms of functionality, we are just moving the function from the parent to the child.
     */ var renderSubOptions = useCallbackExperiment(// eslint-disable-next-line @faire/no-inline-hoc-fc
    function(options) {
        if (options.length === 0) {
            return null;
        }
        return /*#__PURE__*/ _jsxs(SuboptionsRow, {
            children: [
                /*#__PURE__*/ _jsx(Spacer, {
                    size: "2x"
                }),
                /*#__PURE__*/ _jsx(OptionsWrapper, {
                    children: options.map(function(option) {
                        return renderOption(option, true);
                    })
                })
            ]
        });
    }, [
        renderOption
    ], shouldMemoMillionLint);
    /** View comment at line 151 */ var selected = shouldMemoMillionLint ? // eslint-disable-next-line react-hooks/rules-of-hooks
    useFilterSectionSelectedChange(function() {
        return !!option.is_selected;
    }) : !!option.is_selected;
    var disabled = option.results_count === 0 && !selected;
    var intermediate = section.selection_type === IFilterSection.SelectionType.MULTIPLE && !selected && hasSelectedOptions(option);
    var singleSelection = section.selection_type === IFilterSection.SelectionType.SINGLE || section.selection_type === IFilterSection.SelectionType.SINGLE_REQUIRED;
    var filteredSubOptions = React.useMemo(function() {
        return getFilteredOptions(option.sub_options, searchValue);
    }, [
        option.sub_options,
        searchValue
    ]);
    var strictLocalize = useStrictLocalization().strictLocalize;
    var _useFilterStore = useFilterStore([
        "hideOptionsResultCount"
    ]), hideOptionsResultCount = _useFilterStore.data.hideOptionsResultCount;
    var _useState = _sliced_to_array(useState(!intermediate), 2), collapsed = _useState[0], setCollapsed = _useState[1];
    var showSubOptions = !collapsed || searchValue !== "";
    var handleHeaderClick = useCallbackExperiment(function() {
        if (collapsed) {
            onExpand === null || onExpand === void 0 ? void 0 : onExpand(option);
        }
        setCollapsed(!collapsed);
    }, [
        collapsed,
        onExpand,
        option
    ], shouldMemoMillionLint);
    var toggleFilterOption = useCallbackExperiment(function() {
        if (disabled) {
            return;
        }
        if (singleSelection) {
            var _section_options;
            (_section_options = section.options) === null || _section_options === void 0 ? void 0 : _section_options.forEach(function(thisOption) {
                recursivelyClearFilter(thisOption, option.key);
            });
        }
        var isRequired = section.selection_type === IFilterSection.SelectionType.SINGLE_REQUIRED;
        if (isRequired) {
            var _section_options1;
            (_section_options1 = section.options) === null || _section_options1 === void 0 ? void 0 : _section_options1.forEach(function(thisOption) {
                var _option_key;
                recursivelyToggleFilter(thisOption, (_option_key = option.key) !== null && _option_key !== void 0 ? _option_key : "", true);
            });
        } else {
            var _section_options2;
            var toggle = !option.is_selected;
            (_section_options2 = section.options) === null || _section_options2 === void 0 ? void 0 : _section_options2.forEach(function(thisOption) {
                var _option_key;
                recursivelyToggleFilter(thisOption, (_option_key = option.key) !== null && _option_key !== void 0 ? _option_key : "", toggle);
            });
        }
        if (!singleSelection) {
            updateAllSelectableSubOptions(option);
            section.options.forEach(selectIfSelectedAllSubOptions);
        }
        var _option_key, _option_is_selected;
        onChange(section, (_option_key = option.key) !== null && _option_key !== void 0 ? _option_key : "", (_option_is_selected = option.is_selected) !== null && _option_is_selected !== void 0 ? _option_is_selected : false);
        /**
         * Previously, there would be 3 mass re-renders
         * 1. When the FilterOptions was clicked (caused by "isNewFilterSelected" changing from false to true)
         * 2. When the updated filterSections were fetched (caused by "filterSections" changing)
         * 3. "isNewFilterSelected" changing from true to false.
         * Note: some of these re-renders might be batched sometimes
         *
         * Each of these re-renders were causing the FilterOption's Radio / Checkbox "checked" field to re-render causing it to change because "filterSections" has a new reference each time despite being the same value..
         * However, now that we are only updating "filterSections" when the value changes, we don't have a way to trigger a re-render of the FilterOption's Radio / Checkbox "checked" field since the reference doesn't change.
         * So instead, we update the version, causing only the specific FilterOptions that actually changed to re-render instead of all the FilterOptions.
         *
         * We are using a Zustand store here so we can have a selector on top of FilterOption's state to trigger a re-render of the specific FilterOption ONLY if it's selected field has changed.
         * This is hacky and the final optimization is to move the custom useFilterSections store as it's own Zustand store.
         */ useFilterSectionSelectedChange.getState().optionChanged(shouldMemoMillionLint);
    }, [
        disabled,
        onChange,
        option,
        section,
        singleSelection
    ], shouldMemoMillionLint);
    var handleOnMouseDown = function() {
        if (isSearchPage) {
            assignEnableOnMouseDownFilters();
            if (isMouseDownEnabled) {
                toggleFilterOption();
            }
        }
    };
    var handleOnKeyDown = function(event) {
        var keyCode = event.keyCode || event.key;
        if ((keyCode === 32 || keyCode === " ") && isMouseDownEnabled) {
            toggleFilterOption();
        }
    };
    var emptyOnChangeHandler = function() {};
    //TODO(Juliette.li@faire): either ask BE to add formatted_results_count or create one for logged-out category pages.
    var renderLabel = function() {
        return /*#__PURE__*/ _jsxs(FilterOptionText, {
            id: "filter-".concat(option.key),
            children: [
                /*#__PURE__*/ _jsx(FilterOptionTextSpan, {
                    $disabled: disabled,
                    "data-show-result-count": !!(option.formatted_results_count && !hideOptionsResultCount),
                    color: disabled ? Core.text.subdued : Core.text.default,
                    "aria-label": option.display_name,
                    title: option.display_name,
                    children: option.display_name
                }),
                option.formatted_results_count && !hideOptionsResultCount ? /*#__PURE__*/ _jsx(ResultCountText, {
                    color: Core.text.subdued,
                    children: showUnformattedResultsCount && !!option.results_count ? option.results_count : option.formatted_results_count
                }) : null
            ]
        });
    };
    var renderSelectionType = function() {
        var isNotEcoFriendlyOption = !(!isSubOption && section.field_name === IFilterSection.FieldName.ECO_FRIENDLY_PRODUCT_ATTRIBUTES);
        if (singleSelection) {
            return /*#__PURE__*/ _jsx("div", {
                onMouseDown: handleMouseDown(handleOnMouseDown),
                onKeyDown: handleOnKeyDown,
                "data-test-id": "filter-option-wrapper-".concat(option.key),
                children: /*#__PURE__*/ _jsx(Radio, {
                    name: option.key,
                    active: selected,
                    disabled: disabled,
                    onClick: isMouseDownEnabled ? emptyOnChangeHandler : toggleFilterOption,
                    align: "flex-start",
                    children: renderLabel()
                })
            });
        } else if (isNotEcoFriendlyOption) {
            return /*#__PURE__*/ _jsx("div", {
                onMouseDown: handleMouseDown(handleOnMouseDown),
                onKeyDown: handleOnKeyDown,
                "data-test-id": "filter-option-wrapper-".concat(option.key),
                children: /*#__PURE__*/ _jsx(Checkbox, {
                    checked: selected,
                    intermediate: intermediate,
                    disabled: disabled,
                    onChange: isMouseDownEnabled ? emptyOnChangeHandler : toggleFilterOption,
                    align: "flex-start",
                    children: /*#__PURE__*/ _jsx(CBFixWrapper, {
                        children: renderLabel()
                    })
                })
            });
        }
        return /*#__PURE__*/ _jsx(CBFixWrapper, {
            children: renderLabel()
        });
    };
    if (!option.key) {
        return null;
    }
    var _option_display_name, _option_display_name1;
    return /*#__PURE__*/ _jsxs(React.Fragment, {
        children: [
            /*#__PURE__*/ _jsxs(OptionRow, {
                $disabled: disabled,
                $selected: !!option.is_selected || intermediate,
                $twoColumnLayout: false,
                children: [
                    renderSelectionType(),
                    !disabled && filteredSubOptions.length > 0 ? /*#__PURE__*/ _jsx(ChevronWrapper, {
                        onClick: handleHeaderClick,
                        "aria-label": collapsed ? strictLocalize({
                            id: "xFOdil",
                            defaultMessage: [
                                {
                                    type: 0,
                                    value: "Expand "
                                },
                                {
                                    type: 1,
                                    value: "filterNameToken"
                                },
                                {
                                    type: 0,
                                    value: " filter"
                                }
                            ]
                        }, {
                            filterNameToken: (_option_display_name = option.display_name) !== null && _option_display_name !== void 0 ? _option_display_name : option.key
                        }) : strictLocalize({
                            id: "BqeQbg",
                            defaultMessage: [
                                {
                                    type: 0,
                                    value: "Collapse "
                                },
                                {
                                    type: 1,
                                    value: "filterNameToken"
                                },
                                {
                                    type: 0,
                                    value: " filter"
                                }
                            ]
                        }, {
                            filterNameToken: (_option_display_name1 = option.display_name) !== null && _option_display_name1 !== void 0 ? _option_display_name1 : option.key
                        }),
                        children: /*#__PURE__*/ _jsx(StyledChevron, {
                            direction: showSubOptions ? "up" : "down",
                            color: Core.icon.default,
                            width: "12px"
                        })
                    }) : null
                ]
            }),
            showSubOptions ? renderSubOptions(filteredSubOptions) : null
        ]
    });
};
var ChevronWrapper = styled(Button).attrs({
    variant: "tertiary"
}).withConfig({
    componentId: "sc-a77be62a-0"
})([
    "flex-grow:1;justify-content:flex-end;"
]);
var StyledChevron = styled(Chevron).withConfig({
    componentId: "sc-a77be62a-1"
})([
    "float:right;margin-top:2px;"
]);
var FilterOptionTextSpan = styled(Span).withConfig({
    componentId: "sc-a77be62a-2"
})([
    "word-break:break-word;-ms-word-break:break-word;cursor:pointer;&[title]:hover{cursor:pointer;}&:hover{color:",
    ';cursor:pointer;}&[data-show-result-count="false"]{overflow:hidden;text-overflow:ellipsis;display:-webkit-box;-webkit-line-clamp:2;-webkit-box-orient:vertical;}'
], function(props) {
    return props.$disabled ? Core.text.subdued : Core.text.hover;
});
var FilterOptionText = styled(Typography).withConfig({
    componentId: "sc-a77be62a-3"
})([
    "float:left;"
]);
var ResultCountText = styled(Span).withConfig({
    componentId: "sc-a77be62a-4"
})([
    "&&&{margin-left:",
    ";}"
], getSpacing("1x"));
var OptionOrSeeMoreRow = styled(Row).attrs({
    align: "flex-start"
}).withConfig({
    componentId: "sc-a77be62a-5"
})([
    "position:relative;width:100%;padding-bottom:",
    ";"
], getSpacing("1x"));
var OptionRow = styled(OptionOrSeeMoreRow).withConfig({
    componentId: "sc-a77be62a-6"
})([
    "width:",
    ";cursor:",
    ";.radio{border-color:",
    ";}&:hover{.radio{border-color:",
    ";}",
    "{color:",
    ";}}"
], function(param) {
    var twoColumnLayout = param.$twoColumnLayout;
    return twoColumnLayout ? "50%" : "100%";
}, function(param) {
    var disabled = param.$disabled;
    return disabled ? "default" : "pointer";
}, function(param) {
    var disabled = param.$disabled;
    return disabled ? Core.border.subdued : Core.border.default;
}, function(param) {
    var disabled = param.$disabled;
    return disabled ? Core.border.subdued : Core.border.hover;
}, FilterOptionText, function(param) {
    var disabled = param.$disabled;
    return disabled ? Core.text.subdued : Core.text.enabled;
});
var CBFixWrapper = styled.div.withConfig({
    componentId: "sc-a77be62a-7"
})([
    "margin-top:-2px;"
]);
var SuboptionsRow = styled(Row).withConfig({
    componentId: "sc-a77be62a-8"
})([
    "width:100%;"
]);
var OptionsWrapper = styled(Row).attrs({
    align: "center"
}).withConfig({
    componentId: "sc-a77be62a-9"
})([
    "position:relative;width:100%;flex-wrap:wrap;"
]);
